import { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import ChatInput from "./ChatInput";
import { Message } from "../types/types";
import ChatHistory from "./ChatHistory";
import TitleBlock from "./TitleBlock";
import FootNote from "./FootNote";
import {
  onTokenRefresh,
  getValidToken,
  forceTokenRefresh,
} from "../utils/tokenRefresh";

import { useMsal } from "@azure/msal-react";

const WEBSOCKET_BASE_URL = "wss://d2p2qvoi48seem.cloudfront.net";
const SESSION_ID_KEY = "chatSessionIdCXP";

function getOrCreateSessionId() {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = `AAMC-${uuidv4()}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

interface ChatContainerBlkProps {
  idToken: string;
}

const ChatContainerBlk = ({ idToken }: ChatContainerBlkProps) => {
  const { instance } = useMsal();

  const getSessionEmail = (): string => {
    const account =
      instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
    if (!account) return "";
    const claims = account.idTokenClaims as
      | { email?: string; preferred_username?: string }
      | undefined;
    return (
      account.username || claims?.email || claims?.preferred_username || ""
    );
  };

  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const pendingMessageRef = useRef<string | null>(null);
  const connectSocketRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let reconnectTimeout: NodeJS.Timeout | null = null;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;
    let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

    const stopHeartbeat = () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        heartbeatInterval = null;
      }
    };

    const startHeartbeat = (socket: WebSocket) => {
      stopHeartbeat();
      heartbeatInterval = setInterval(
        () => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ action: "ping" }));
          } else {
            stopHeartbeat();
            if (socket.readyState === WebSocket.CLOSED) {
              reconnectAttempts = 0;
              if (!getValidToken()) {
                const refreshed = forceTokenRefresh();
                if (!refreshed) {
                  connectSocket();
                }
              } else {
                connectSocket();
              }
            }
          }
        },
        2 * 60 * 1000,
      );
    };

    const connectSocket = () => {
      if (
        socketRef.current &&
        (socketRef.current.readyState === WebSocket.OPEN ||
          socketRef.current.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      const currentToken = localStorage.getItem("idToken") || idToken || "";
      if (!currentToken) return;

      const sessionId = getOrCreateSessionId();
      const socket = new WebSocket(
        `${WEBSOCKET_BASE_URL}?token=${encodeURIComponent(currentToken)}&sessionId=${encodeURIComponent(sessionId)}`,
      );
      socketRef.current = socket;

      socket.onopen = () => {
        reconnectAttempts = 0;
        setIsLoading(false);
        setIsThinking(false);
        startHeartbeat(socket);
        if (pendingMessageRef.current) {
          const pending = pendingMessageRef.current;
          pendingMessageRef.current = null;
          const userMessage: Message = {
            action: "invoke",
            userInput: pending,
            sessionId: getOrCreateSessionId(),
            sender: "user",
            userEmail: getSessionEmail(),
          };
          setIsLoading(true);
          socket.send(JSON.stringify(userMessage));
        }
      };

      socket.onclose = () => {
        stopHeartbeat();
        reconnectAttempts++;
        if (reconnectAttempts > MAX_RECONNECT_ATTEMPTS) return;
        const delay = Math.min(10000, reconnectAttempts * 2000);
        reconnectTimeout = setTimeout(connectSocket, delay);
      };

      socket.onerror = () => {
        socket.close();
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "ping" || data.type === "pong") return;

          if (data.type === "typing") {
            setIsThinking(true);
            return;
          }

          let botMessageText = "";
          if (typeof data.botResponse === "string") {
            botMessageText = data.botResponse;
          } else if (data.type === "complete" && data.message) {
            botMessageText = data.message;
          }

          if (!botMessageText) return;

          const botMessage: Message = {
            sender: "bot",
            userInput: botMessageText,
            sessionId: data.sessionId || getOrCreateSessionId(),
            messageId: data.messageId || "",
            buttons: data.buttons || [],
            feedbackButtons: data.feedbackButtons || [],
            parsedSql: data.parsedSql || null,
            s3Key: data.s3Key || null,
          };

          setMessages((prev) => [...prev, botMessage]);
          setIsLoading(false);
          setIsThinking(false);
          pendingMessageRef.current = null;
        } catch (err) {
          console.error("Error parsing WS message:", err);
          setIsLoading(false);
          setIsThinking(false);
        }
      };
    };

    connectSocketRef.current = connectSocket;
    connectSocket();

    const handleFocus = () => {
      if (
        !socketRef.current ||
        socketRef.current.readyState === WebSocket.CLOSED
      ) {
        reconnectAttempts = 0;
        connectSocket();
      }
    };

    // Token refresh: DO NOT close the existing socket.
    // The Lambda authorizer only validates at $connect — the existing connection
    // remains valid for its lifetime. The new token will be used automatically
    // the next time the socket needs to reconnect.
    onTokenRefresh((_newToken: string) => {
      // Token already saved to localStorage by tokenRefresh.ts
      // Existing WebSocket connection continues uninterrupted
    });

    window.addEventListener("focus", handleFocus);

    return () => {
      stopHeartbeat();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      window.removeEventListener("focus", handleFocus);
      socketRef.current?.close();
    };
  }, []);

  useEffect(() => {
    function updateVH() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    }
    updateVH();
    window.addEventListener("resize", updateVH);
    window.addEventListener("orientationchange", updateVH);
    return () => {
      window.removeEventListener("resize", updateVH);
      window.removeEventListener("orientationchange", updateVH);
    };
  }, []);

  const onSubmitHandler = (message?: string) => {
    const val =
      typeof message === "string" ? message.trim() : inputValue.trim();
    if (!val) return;

    const userMessage: Message = {
      action: "invoke",
      userInput: val,
      sessionId: getOrCreateSessionId(),
      sender: "user",
      userEmail: getSessionEmail(),
    };

    setIsSubmitted(true);
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    // If token is expired, queue and force refresh — the new connection will flush it
    const validToken = getValidToken();
    if (!validToken) {
      pendingMessageRef.current = val;
      const refreshed = forceTokenRefresh();
      if (!refreshed) {
        if (
          !socketRef.current ||
          socketRef.current.readyState === WebSocket.CLOSED
        ) {
          connectSocketRef.current?.();
        }
      }
      return;
    }

    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      pendingMessageRef.current = val;
      if (
        !socketRef.current ||
        socketRef.current.readyState === WebSocket.CLOSED
      ) {
        connectSocketRef.current?.();
      }
      return;
    }

    setIsLoading(true);
    pendingMessageRef.current = null;
    socketRef.current.send(JSON.stringify(userMessage));
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className="chat-container-canvas-blk">
      <TitleBlock isSubmitted={isSubmitted} />
      <ChatHistory
        messages={messages}
        bottomRef={bottomRef}
        isLoading={isLoading}
        isThinking={isThinking}
        onSubmit={onSubmitHandler}
        setInputValue={setInputValue}
      />
      <ChatInput
        messages={messages}
        inputValue={inputValue}
        setInputValue={setInputValue}
        onSubmit={onSubmitHandler}
        isDisabled={isLoading || isThinking}
        isSubmitted={isSubmitted}
      />
      <FootNote />
    </div>
  );
};

export default ChatContainerBlk;
